import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import LeadForm from "../../../../Components/Form/LeadForm";
import { db, auth } from "../../../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { showSuccessToast, showErrorToast } from "../../../../toastmsg";

const AddLead = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState<{
    uid: string;
    displayName: string;
    role: string;
  } | null>(null);

  // Fetch current user info
  useEffect(() => {
    let isMounted = true;
    const user = auth.currentUser;
    if (!user) return;

    const fetchUserInfo = async () => {
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists() && isMounted) {
          const data = userDoc.data();
          setCurrentUser({
            uid: user.uid,
            displayName: data.displayName || "",
            role: data.role || "",
          });
        }
      } catch (err) {
        console.error("Error fetching user info:", err);
        if (isMounted) {
          showErrorToast("Failed to load user information");
        }
      }
    };

    fetchUserInfo();

    return () => {
      isMounted = false;
    };
  }, []);

  // ✅ Helper: format date as dd-mm-yyyy
  const formatDate = (input: string | Date): string => {
    if (!input) return "-";
    const date = input instanceof Date ? input : new Date(input);
    if (isNaN(date.getTime())) return "-";
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
  };

  // ✅ Normalize phone numbers for comparison
  const normalizePhone = (phone: string): string => {
    if (!phone) return "";
    let normalized = phone.replace(/\D/g, ""); // remove non-digits
    if (normalized.startsWith("60")) {
      normalized = normalized.substring(2); // remove country code
    } else if (normalized.startsWith("0")) {
      normalized = normalized.substring(1); // remove leading zero
    }
    return normalized;
  };

  // ✅ Check for duplicate using Cloud Function
  // ✅ Handle form submission from LeadForm
  const handleAddLead = async (formData: any) => {
    setLoading(true);

    try {
      const normalizedInputPhone = normalizePhone(formData.phoneNumber || "");
      const isDuplicate = false;

      // ✅ Check for duplicates using Cloud Function
      const duplicateResult = { field: "", value: "" };

      if (isDuplicate) {
        console.log(
          "Duplicate detected:",
          duplicateResult.field,
          duplicateResult.value,
        );
        // We no longer block the save. We just mark it as "Duplicate".
      }

      // ✅ Auto-assign to sales users
      const isSalesUser =
        currentUser?.role === "sales" ||
        currentUser?.role === "part-time sales";

      const newLead = {
        name: formData.name || "",
        ic: formData.ic || "",
        dob: formData.dob || "",
        gender: formData.gender || "",
        country: formData.country || "Malaysia",
        countryCode: formData.countryCode || "+60",
        phoneNumber: normalizedInputPhone,
        email: formData.email || "",
        status: (() => {
          // If duplicate confirmed by Cloud Function, force status to "Duplicate"
          if (isDuplicate) {
            return "Duplicate";
          }

          // Admin user logic: if no assignee, Unassigned
          if (!formData.assignee && !formData.assigneeId) {
            return "Unassigned";
          }

          // Otherwise use form status or default to New
          return formData.status || "New";
        })(),
        assignee:
          formData.assignee ||
          (isSalesUser ? currentUser?.displayName || "" : ""),
        assigneeId:
          formData.assigneeId || (isSalesUser ? currentUser?.uid || "" : ""),
        leadSource: formData.leadSource || "Online",
        campaign: formData.campaign || "-",
        createdDate: new Date().toISOString(),
        // firstTouchedDate: formatDate(formData.firstTouchedDate || ""),
        // lastTouchedDate: formatDate(formData.lastTouchedDate || ""),
        location: formData.location || "",
        preferredUniversity: formData.preferredUniversity || "",
        intake: formData.intake || "",
        campusVisit: formData.campusVisit || "",
        videoCall: formData.videoCall || "",
        familyFinancials: formData.familyFinancials || "",
        studentResult: formData.studentResult || "",
        estimatedResult: formData.estimatedResult || "",
        careerTestResult:
          formData.careerTestResult &&
          typeof formData.careerTestResult === "object"
            ? formData.careerTestResult
            : undefined,
        overseas: formData.overseas || "",
        interestedCourses: formData.interestedCourses || "",
        qualification: formData.qualification || "",
        schoolName: formData.schoolName || "",
        reason: formData.reason || "",
        remarks: formData.remarks || "",
        uploadedResult: formData.uploadedResult || "",
      };

      // ✅ Always add a new lead document
      const functions = getFunctions(undefined, "asia-southeast1");
      const upsertStaffLead = httpsCallable(functions, "upsertStaffLead");
      const result = await upsertStaffLead({ leadData: newLead });
      const resultData = result.data as any;

      // ✅ Show success toast and navigate back
      if (resultData.action === "review-required") {
        if (resultData.reviewType === "protected-match") {
          showSuccessToast(
            "Authenticated lead found. No lead data was changed; the submission is pending review.",
          );
        } else if (resultData.reviewType === "ambiguous-match") {
          showSuccessToast(
            "Multiple possible leads found. The submission is pending review.",
          );
        } else {
          showSuccessToast(
            "Possible duplicate found. The submission is pending review.",
          );
        }
      } else {
        showSuccessToast("Lead added successfully!");
      }
      navigate("/leads");
    } catch (error: any) {
      console.error("Error adding lead:", error);
      console.error("🔥 Error message:", error?.message);
      showErrorToast(error.message || "Failed to add lead");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle cancel
  const handleCancel = () => {
    navigate("/leads");
  };

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Add New Lead</h2>
      <LeadForm
        initialData={{ country: "Malaysia", countryCode: "+60" }}
        onSubmit={handleAddLead}
        onCancel={handleCancel}
        disabled={loading}
      />
    </div>
  );
};

export default AddLead;
